import { useEffect, useState } from "react";
import ProductListStyles from "./ProductListStyles.module.css";
import Input from "../input/Input";

type VariantOption = { id: number; title: string; quantity: number };
type Variant = { id: number; title: string; options: VariantOption[] };

type MerchVariantProps = {
  setCreateProductForm: React.Dispatch<
    React.SetStateAction<Record<string, any>>
  >;
  createProductForm: any;
};

const MerchVariant = ({
  setCreateProductForm,
  createProductForm,
}: MerchVariantProps) => {
  const [variants, setVariants] = useState<Variant[]>(() => {
    // Initialize with quantity field if not present (for backwards compatibility)
    const existingVariants = createProductForm.variant || [];
    return existingVariants.map((variant: any) => ({
      ...variant,
      options: variant.options?.map((option: any) => ({
        ...option,
        quantity: option.quantity ?? 0,
      })) || [],
    }));
  });

  // Sync with parent state whenever variants change
  useEffect(() => {
    setCreateProductForm((prev: any) => ({
      ...prev,
      variant: variants,
    }));
  }, [variants, setCreateProductForm]);

  const addVariant = () => {
    setVariants([
      ...variants,
      {
        id: Date.now(),
        title: "",
        options: [{ id: Date.now(), title: "", quantity: 0 }],
      },
    ]);
  };

  const addOption = (variantId: number) => {
    setVariants((prev) =>
      prev.map((variant) =>
        variant.id === variantId
          ? {
              ...variant,
              options: [
                ...variant.options,
                { id: Date.now(), title: "", quantity: 0 },
              ],
            }
          : variant
      )
    );
  };

  const deleteVariant = (variantId: number) => {
    setVariants(variants.filter((variant) => variant.id !== variantId));
  };

  const deleteOption = (variantId: number, optionId: number) => {
    setVariants((prev) =>
      prev.map((variant) =>
        variant.id === variantId
          ? {
              ...variant,
              options: variant.options.filter(
                (option) => option.id !== optionId
              ),
            }
          : variant
      )
    );
  };

  const handleVariantChange = (variantId: number, newTitle: string) => {
    setVariants((prev) =>
      prev.map((variant) =>
        variant.id === variantId ? { ...variant, title: newTitle } : variant
      )
    );
  };

  const handleOptionChange = (
    variantId: number,
    optionId: number,
    newTitle: string
  ) => {
    setVariants((prev) =>
      prev.map((variant) =>
        variant.id === variantId
          ? {
              ...variant,
              options: variant.options.map((option) =>
                option.id === optionId ? { ...option, title: newTitle } : option
              ),
            }
          : variant
      )
    );
  };

  const handleOptionQuantityChange = (
    variantId: number,
    optionId: number,
    newQuantity: number
  ) => {
    setVariants((prev) =>
      prev.map((variant) =>
        variant.id === variantId
          ? {
              ...variant,
              options: variant.options.map((option) =>
                option.id === optionId
                  ? { ...option, quantity: newQuantity }
                  : option
              ),
            }
          : variant
      )
    );
  };

  return (
    <div className={ProductListStyles.merchContainer}>
      {variants?.length >= 1 && <h1>Variants</h1>}

      {variants.map((variant, index) => (
        <div key={variant.id} className={ProductListStyles.merchBlock}>
          <button
            type="button"
            className={ProductListStyles.deleteVariantButton}
            onClick={() => deleteVariant(variant.id)}
            aria-label={`Delete variant ${index + 1}`}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
          <h3>Variant {index + 1}</h3>
          <Input
            type="text"
            label="Variant Title"
            onChangeHandler={(e) =>
              handleVariantChange(variant.id, e.target.value)
            }
            name="variantTitle"
            value={variant.title}
          />
          <div className={ProductListStyles.containerOptionBlk}>
            {variant.options.map((option, optionIndex) => (
              <div
                className={ProductListStyles.optionBlockFlexBox}
                key={option.id}
              >
                <div className={ProductListStyles.optionInputGroup}>
                  <div className={ProductListStyles.optionNameInput}>
                    <Input
                      type="text"
                      label={optionIndex === 0 ? "Size" : ""}
                      value={option.title}
                      onChangeHandler={(e) =>
                        handleOptionChange(variant.id, option.id, e.target.value)
                      }
                    />
                  </div>
                  <div className={ProductListStyles.optionQuantityInput}>
                    <Input
                      type="number"
                      label={optionIndex === 0 ? "Qty" : ""}
                      value={option.quantity.toString()}
                      onChangeHandler={(e) =>
                        handleOptionQuantityChange(
                          variant.id,
                          option.id,
                          Math.max(0, parseInt(e.target.value) || 0)
                        )
                      }
                      maxLength={6}
                    />
                  </div>
                </div>
                <div className={ProductListStyles.optionDeleteButton}>
                  <button
                    type="button"
                    className={ProductListStyles.deleteOptionButton}
                    onClick={() => deleteOption(variant.id, option.id)}
                    aria-label={`Delete option ${option.title || optionIndex + 1}`}
                  >
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className={ProductListStyles.addOptionButton}
              onClick={() => addOption(variant.id)}
            >
              + Add Size Option
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className={ProductListStyles.manageVariantButton}
        onClick={addVariant}
      >
        + Add Variant
      </button>
    </div>
  );
};

export default MerchVariant;
